// The one sleep.
//
// Thirteen definitions of this existed when plan 101 slice 04 counted them, and
// the census in `scripts/one-impl.test.ts` is what keeps it at one. They were not
// all the same: some resolved late after their caller had already given up,
// because `new Promise((r) => setTimeout(r, ms))` has no way to hear an abort.
// That is the property this module adds and the twins lacked.

/**
 * A pause. Overridable in tests so a backoff ladder costs no wall clock.
 *
 * The signal is part of the TYPE, not just of {@link sleep}: a fake that ignores
 * it would let a test pass over a ladder that cannot be cancelled in production.
 */
export type Sleep = (ms: number, signal?: AbortSignal) => Promise<void>;

/**
 * Resolve after `ms`, or REJECT as soon as `signal` aborts — whichever is first.
 *
 * The rejection is the point. A plain `setTimeout` promise resolves on its own
 * schedule, so a caller that raced it against a cancellation still had a live
 * timer holding the event loop and a continuation that ran after the work was
 * abandoned. Here the timer is cleared on both exits and the listener is removed
 * with it, so nothing outlives the call.
 *
 * `unref()` where the runtime has it: a pending sleep must not by itself keep a
 * process alive. It is `?.` because the DOM lib's `setTimeout` returns a number.
 */
export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted === true) return Promise.reject(signal.reason ?? abortError());
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    (timer as { unref?: () => void }).unref?.();
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal?.reason ?? abortError());
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * The reason an abort with no reason of its own rejects with.
 *
 * `AbortSignal.abort()` populates `reason` for us, but a signal aborted by hand
 * (`controller.abort()` on some runtimes, or a hand-rolled fake in a test) can
 * carry `undefined`, and a promise rejecting with `undefined` is the hardest
 * possible thing to debug.
 */
function abortError(): Error {
  const err = new Error('sleep aborted');
  err.name = 'AbortError';
  return err;
}
